import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import LanguageIcon from "@mui/icons-material/Language";
import Cookies from "js-cookie";
import i18n from "i18next";
import { Divider, FormControl, Select, SelectChangeEvent } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import Box from "@mui/material/Box";
import { useLoginStatus } from "../../../context/LoginStatusContext";
import Typography from "@mui/material/Typography";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import ai_language from "../../../json/ai_languages.json";
import countries from "../../../json/countries.json";

export default function LanguageSwitcher({ color }: { color?: string }) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { isLoggedIn, user, refreshUser } = useLoginStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [aiLanguage, setAiLanguage] = useState(user?.ai_language || "");
  const [country, setCountry] = useState(user?.country || "");
  const [currentUILanguage, setCurrentUILanguage] = useState(i18n.language);

  const aiLanguagesList = ai_language;
  const countriesList = countries;

  useEffect(() => {
    if (user) {
      setAiLanguage(user.ai_language || "");
      setCountry(user.country || "");
    }
  }, [user]);

  useEffect(() => {
    setCurrentUILanguage(i18n.language);
  }, [location]);

  const switchLanguage = (language: string) => {
    setLoading(true);
    i18n.changeLanguage(language);
    Cookies.set("ui_language", language, { expires: 180 });
    setCurrentUILanguage(language);
    handleClose();

    const currentPath = location.pathname.split("/").slice(2).join("/");
    let newPath = `/${language}/${currentPath}`;
    if (newPath.slice(-1) === "/") {
      newPath = newPath.slice(0, -1);
    }
    navigate(newPath, { replace: true });

    setLoading(false);
  };

  const handleAILanguageChange = (event: SelectChangeEvent<string>) => {
    const lang = event.target.value;
    setAiLanguage(lang);

    const csrftoken = Cookies.get("csrftoken");
    const config = { headers: { "X-CSRFToken": csrftoken } };

    axios
      .patch("/api/v1/users/user", { ai_language: lang }, config)
      .then(() => {
        refreshUser();
      })
      .catch((error) => {
        console.error("Error setting AI language:", error);
      });
  };

  const handleCountryChange = (event: SelectChangeEvent<string>) => {
    const countryCode = event.target.value;
    setCountry(countryCode);

    const csrftoken = Cookies.get("csrftoken");
    const config = { headers: { "X-CSRFToken": csrftoken } };

    axios
      .patch("/api/v1/users/user", { country: countryCode }, config)
      .then(() => {
        refreshUser();
      })
      .catch((error) => {
        console.error("Error setting country:", error);
      });
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <IconButton
        size="large"
        aria-label="account of current user"
        aria-haspopup="true"
        aria-controls="language-menu"
        onClick={handleClick}
        sx={{ color: color }}
      >
        <LanguageIcon />
      </IconButton>
      <Menu id="language-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
        <Box sx={{ minWidth: 280, p: 2 }}>
          {/* UI言語 */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("preferences.settings.uiLanguage")}
            </Typography>
            <MenuItem onClick={() => switchLanguage("en")}>
              <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                <Box sx={{ width: 24, display: "flex", justifyContent: "flex-start" }}>
                  {currentUILanguage === "en" && <CheckIcon sx={{ fontSize: 16, color: "success.main" }} />}
                </Box>
                <Box sx={{ flexGrow: 1 }}>English (English)</Box>
              </Box>
            </MenuItem>
            <MenuItem onClick={() => switchLanguage("ja")}>
              <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                <Box sx={{ width: 24, display: "flex", justifyContent: "flex-start" }}>
                  {currentUILanguage === "ja" && <CheckIcon sx={{ fontSize: 16, color: "success.main" }} />}
                </Box>
                <Box sx={{ flexGrow: 1 }}>Japanese (日本語)</Box>
              </Box>
            </MenuItem>
          </Box>

          {isLoggedIn && (
            <>
              <Box sx={{ mt: 3 }}>
                {/* AI言語 */}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t("preferences.settings.aiLanguage")}
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <Select value={aiLanguage} onChange={handleAILanguageChange} size="small">
                    {aiLanguagesList.map((language) => (
                      <MenuItem key={language.language_code} value={language.language_code}>
                        {language.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ mt: 2 }}>
                {/* 国 */}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t("preferences.settings.country")}
                </Typography>
                <FormControl fullWidth>
                  <Select value={country} onChange={handleCountryChange} size="small" MenuProps={{ PaperProps: { sx: { maxHeight: "50vh" } } }}>
                    {countriesList.map((country) => (
                      <MenuItem key={country.country_code} value={country.country_code}>
                        {country.country_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </>
          )}
        </Box>
      </Menu>
    </>
  );
}

export const changeUILanguage = (language: string) => {
  i18n.changeLanguage(language);
  Cookies.set("ui_language", language, { expires: 180 });
};
